import { useEffect, useState } from "react";
import { Package, ChevronRight, ChevronDown } from "lucide-react";
import { img } from "@/lib/media";
import { isEachUnit } from "@/lib/uom";

export default function ItemsSummary({ items = [], products = {}, testid }) {
  const [pos, setPos] = useState(null);
  const [open, setOpen] = useState({});
  const list = items || [];
  const totalQty = list.reduce((s, i) => s + (i.quantity || 0), 0);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => { window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); };
  }, [pos]);

  const openAt = (e) => { const r = e.currentTarget.getBoundingClientRect(); const up = (window.innerHeight - r.bottom) < 360; setPos({ x: r.left, y: up ? r.top : r.bottom, up }); setOpen({}); };
  const toggle = (idx) => setOpen((o) => ({ ...o, [idx]: !o[idx] }));

  const details = (it) => {
    const p = products[it.product_id] || {};
    return {
      image: it.image || p.images?.[0]?.url,
      name: it.name || p.name || "—",
      uom: isEachUnit(it.uom) ? "" : (it.uom || ""),
      part_number: it.part_number || p.part_number || "—",
      category: p.category || "—",
      brand: it.brand || p.brand || "—",
      description: p.description || "",
      price: Number(it.price) || 0,
      qty: it.quantity || 0,
      line_total: it.line_total != null ? Number(it.line_total) : (Number(it.price) || 0) * (it.quantity || 0),
      missing: !products[it.product_id],
    };
  };

  return (
    <div data-testid={testid}>
      <div className="text-sm leading-snug flex items-center gap-1.5">
        <span className="font-semibold whitespace-nowrap">{totalQty} Item{totalQty === 1 ? "" : "s"}</span>
        {list.length > 0 && <button onClick={openAt} data-testid={testid ? `${testid}-expand` : undefined} title="View item details" className="shrink-0 h-6 w-6 rounded-md hover:bg-zinc-100 flex items-center justify-center text-brand"><ChevronDown size={15} /></button>}
      </div>

      {pos && <>
        <div className="fixed inset-0 z-40" onClick={() => setPos(null)} onWheel={() => setPos(null)} onTouchMove={() => setPos(null)} />
        <div style={{ position: "fixed", left: Math.min(pos.x, window.innerWidth - 340), ...(pos.up ? { bottom: window.innerHeight - pos.y + 4 } : { top: pos.y + 4 }) }} className="w-[330px] max-h-[70vh] overflow-y-auto bg-white border border-zinc-200 rounded-xl shadow-xl z-50 p-2" data-testid={testid ? `${testid}-popover` : undefined}>
          <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-400 px-2 py-1.5 flex items-center gap-1.5"><Package size={13} />{totalQty} Item{totalQty === 1 ? "" : "s"} · {list.length} product{list.length === 1 ? "" : "s"}</p>
          <ul className="space-y-0.5">
            {list.map((it, idx) => { const d = details(it); const isOpen = !!open[idx]; return (
              <li key={idx} className="rounded-lg border border-transparent hover:border-zinc-100">
                <button onClick={() => toggle(idx)} data-testid={testid ? `${testid}-item-${idx}` : undefined} className="w-full flex items-center gap-2 px-2 py-1.5 text-left">
                  {isOpen ? <ChevronDown size={14} className="text-zinc-400 shrink-0" /> : <ChevronRight size={14} className="text-zinc-400 shrink-0" />}
                  <span className="h-8 w-8 rounded-md bg-zinc-100 overflow-hidden shrink-0 flex items-center justify-center text-zinc-300">{d.image ? <img src={img(d.image)} alt="" className="w-full h-full object-cover" /> : <Package size={14} />}</span>
                  <span className="min-w-0 flex-1"><span className="block text-sm font-semibold truncate">{d.name}</span><span className="block text-[11px] text-zinc-400 truncate">{d.uom ? `${d.uom} · ` : ""}×{d.qty}</span></span>
                  <span className="text-sm font-semibold whitespace-nowrap">${d.line_total.toFixed(2)}</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1" data-testid={testid ? `${testid}-detail-${idx}` : undefined}>
                    <div className="rounded-lg bg-zinc-50 p-3 space-y-2">
                      {d.image && <img src={img(d.image)} alt={d.name} className="w-full h-28 object-contain rounded-md bg-white" />}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        <KV k="Part #" v={d.part_number} mono />
                        <KV k="UOM" v={d.uom} />
                        <KV k="Brand" v={d.brand} />
                        <KV k="Category" v={d.category} />
                        <KV k="Unit price" v={`$${d.price.toFixed(2)}`} />
                        <KV k="Quantity" v={`×${d.qty}`} />
                        <KV k="Line total" v={`$${d.line_total.toFixed(2)}`} />
                      </div>
                      {d.description && <p className="text-xs text-zinc-500 leading-relaxed border-t border-zinc-200 pt-2">{d.description}</p>}
                      {d.missing && <p className="text-[11px] text-amber-600">Product no longer in catalog — showing saved invoice details.</p>}
                    </div>
                  </div>
                )}
              </li>
            ); })}
          </ul>
        </div>
      </>}
    </div>
  );
}

function KV({ k, v, mono }) {
  return <div><span className="block text-[10px] uppercase tracking-wider font-bold text-zinc-400">{k}</span><span className={`block text-xs text-zinc-700 font-medium ${mono ? "font-mono" : ""}`}>{v || "—"}</span></div>;
}
